import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User } from '../interfaces/User';

const USER_STORAGE_KEY = 'user';
const TOKEN_STORAGE_KEY = 'accessToken';

type StoredUser = Pick<User, 'id' | 'username' | 'role' | 'email'>;

function toStoredUser(user: User): StoredUser {
  return { id: user.id, username: user.username, role: user.role, email: user.email };
}

export const useAuthStore = defineStore('auth', () => {
  const storedUser = localStorage.getItem(USER_STORAGE_KEY);
  const initialUser: StoredUser | null = storedUser ? JSON.parse(storedUser) : null;

  const user = ref<StoredUser | null>(initialUser);
  const token = ref<string | null>(localStorage.getItem(TOKEN_STORAGE_KEY));
  const isVersionLogOpen = ref(false);

  const isAuthenticated = computed(() => !!user.value?.role);

  function setUser(newUser: User | null) {
    if (newUser) {
      const slim = toStoredUser(newUser);
      user.value = slim;
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(slim));
    } else {
      user.value = null;
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  }

  // Bearer-token fallback for cross-site cookie blocking (iOS/Safari on
  // *.onrender.com). Persisted so it survives reloads and rehydrates the
  // Authorization header via the request interceptor.
  function setToken(newToken: string | null) {
    token.value = newToken;
    if (newToken) {
      localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }

  function logoutUser() {
    user.value = null;
    token.value = null;
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  function openVersionLog() {
    isVersionLogOpen.value = true;
  }

  function closeVersionLog() {
    isVersionLogOpen.value = false;
  }

  return {
    user,
    token,
    isAuthenticated,
    isVersionLogOpen,
    setUser,
    setToken,
    logoutUser,
    openVersionLog,
    closeVersionLog,
  };
});

